import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw, Plus, Save, TriangleAlert } from 'lucide-react';
import { capexService } from '../../services/capexService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import PickerPopover from '../../components/PickerPopover';
import CapexItemsModal from './CapexItemsModal';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';

// =============================================================================
// Capital › Capital Programme   (LIVE — replaces a dummy-data mockup)
//
// A FIVE-YEAR PROGRAMME, NOT A LIST OF PURCHASES. What was here before held one
// amount against one month, which is a purchase order. A capital plan spends
// across the year and keeps spending into the years after it, and the five-year
// shape is what a board looks at before it looks at any single month.
//
// THREE GRAINS ON ONE ROW, AND THEY MEAN DIFFERENT THINGS:
//
//   Opening      spend already incurred in PRIOR years — work in progress
//                brought in. A balance, not a movement.
//   P1..P12      the budget year, by month. This is the only part any
//                statement reads.
//   Y2..Y5       annual. PLANNING ONLY.
//
// THE RULE THIS PAGE EXISTS TO MAKE VISIBLE: years 2-5 never reach the P&L,
// cash flow or balance sheet. Those are budget-year statements. The column
// group is drawn with a visible break and labelled so nobody expects the cash
// flow to move when they type in Y3.
//
// OPENING BALANCE IS COST BUT NOT CASH, which is the subtlest thing here.
// Money spent last year on a project capitalising this year is part of what the
// asset cost — so it depreciates — but it left the bank in a prior year. It is
// in the depreciable cost and not in this year's cash flow, and the footnote
// says so because the two figures otherwise look like they disagree.
//
// CAPITALISATION IS NOT SPEND. Cash leaves as the project is built; the P&L
// sees depreciation from the month it is switched on. The server refuses a
// project that capitalises before its spend finishes, because that quietly
// depreciates an asset nobody has paid for yet.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white disabled:opacity-40 transition-colors';

const fmtR = (n) => {
  const v = Math.round(Number(n) || 0);
  return v === 0 ? '—' : v.toLocaleString();
};

const T_OPENING = { bgCell: 'rgba(100, 116, 139, 0.10)' };
// Out-years are tinted differently on purpose: they are a different kind of
// number from the twelve months beside them and must not read as continuous.
const T_OUTYEAR = { bgCell: 'rgba(168, 85, 247, 0.10)' };
const T_TOTAL = { bgCell: 'rgba(37, 99, 235, 0.13)', baseFontStyle: '700 12px' };
const T_FOOT = { bgCell: 'rgba(100, 116, 139, 0.18)', baseFontStyle: '700 12px' };
const T_NEW = { bgCell: 'rgba(16, 185, 129, 0.10)' };
const T_MUTED = { textDark: '#94a3b8' };

const STATUS_LABEL = {
  PROPOSED: 'Proposed', APPROVED: 'Approved', IN_PROGRESS: 'In progress',
  COMPLETE: 'Complete', ON_HOLD: 'On hold',
};

const OUT_YEARS = [2, 3, 4, 5];
const EMPTY = { rows: [], periods: [], years: [], picks: {}, statuses: [], financialYear: null };

const blankRow = () => ({
  _new: true, _key: `new-${Math.random().toString(36).slice(2)}`,
  capex_ref: '', description: '', project_status: 'PROPOSED',
  opening_balance: 0, periods: Array(12).fill(0), years: { 2: 0, 3: 0, 4: 0, 5: 0 },
});

export default function CapitalProjects() {
  const navigate = useNavigate();
  const { selectedVersion, fyStartMonth } = useAppContext();

  const [data, setData] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [picker, setPicker] = useState(null);
  const [dirty, setDirty] = useState(new Set());
  // THE WIP ACCOUNT YOU ARE BUDGETING AGAINST. One choice at the top rather
  // than a column nobody fills in: in practice a company has one capital WIP
  // account, and making it a per-row decision means half the projects end up
  // with none and their spend disappears until capitalisation.
  const [wipAccount, setWipAccount] = useState('');
  const [itemsFor, setItemsFor] = useState(null);   // the project being broken down

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await capexService.get();
      const d = res.data || EMPTY;
      setData(d);

      const perByCapex = new Map();
      (d.periods || []).forEach((p) => {
        if (!perByCapex.has(p.capex_id)) perByCapex.set(p.capex_id, Array(12).fill(0));
        perByCapex.get(p.capex_id)[p.financial_period - 1] = p.amount;
      });
      const yrByCapex = new Map();
      (d.years || []).forEach((y) => {
        if (!yrByCapex.has(y.capex_id)) yrByCapex.set(y.capex_id, {});
        yrByCapex.get(y.capex_id)[y.year_offset] = y.amount;
      });

      const itemsByCapex = new Map();
      (d.items || []).forEach((i) => {
        if (!itemsByCapex.has(i.capex_id)) itemsByCapex.set(i.capex_id, []);
        itemsByCapex.get(i.capex_id).push(i);
      });

      setRows((d.rows || []).map((r) => ({
        ...r,
        _key: r.capex_id,
        periods: perByCapex.get(r.capex_id) || Array(12).fill(0),
        years: { 2: 0, 3: 0, 4: 0, 5: 0, ...(yrByCapex.get(r.capex_id) || {}) },
        items: itemsByCapex.get(r.capex_id) || [],
      })));
      // Default the header selector to whatever the projects already use, or
      // to the only WIP account if there is just one.
      const used = [...new Set((d.rows || []).map((r) => r.gl_wip_account).filter(Boolean))];
      setWipAccount(used.length === 1 ? used[0]
        : (d.picks?.wipAccounts?.length === 1 ? d.picks.wipAccounts[0].account_code : (used[0] || '')));
      setDirty(new Set());
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the capital programme.');
      setData(EMPTY);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  // Memoised: a fresh {} each render would re-create the click handler that
  // builds the pick lists, on every keystroke in the grid.
  const picks = useMemo(() => data.picks || {}, [data.picks]);
  const fy = data.financialYear;

  // Out-year columns are labelled with REAL YEARS where the version knows its
  // financial year. year_offset is stored relative so a version can be rolled
  // forward; turning it into a calendar year is purely a display concern, and
  // "FY2028" is a great deal more use to a reader than "Year 3".
  const yearLabel = useCallback((off) => (fy ? `FY${fy + off - 1}` : `Year ${off}`), [fy]);

  const columns = useMemo(() => ([
    { id: 'capex_ref', title: 'Ref', width: 110 },
    { id: 'description', title: 'Project', width: 260, grow: 1 },
    { id: 'site_code', title: 'Site', width: 110 },
    { id: 'asset_class_code', title: 'Class', width: 120 },
    { id: 'project_status', title: 'Status', width: 120 },
    { id: 'opening_balance', title: 'Opening', width: 110 },
    ...PERIODS.map((p) => ({ id: `P${p}`, title: periodLabel(p, fyStartMonth), width: 86 })),
    { id: 'year1', title: 'Year 1', width: 116 },
    ...OUT_YEARS.map((y) => ({ id: `Y${y}`, title: yearLabel(y), width: 104 })),
    { id: 'total', title: 'Programme', width: 124 },
    { id: 'cap', title: 'Capitalise', width: 96 },
    { id: 'items', title: 'Items', width: 110 },
    { id: 'del', title: '', width: 44 },
  ]), [fyStartMonth, yearLabel]);

  const colIds = useMemo(() => columns.map((c) => c.id), [columns]);

  const year1Of = (r) => r.periods.reduce((a, b) => a + (Number(b) || 0), 0);
  const outYearsOf = (r) => OUT_YEARS.reduce((a, y) => a + (Number(r.years[y]) || 0), 0);
  const programmeOf = (r) => (Number(r.opening_balance) || 0) + year1Of(r) + outYearsOf(r);

  // The foot row totals every column. A capital plan is read down the columns
  // as often as across the rows — "what do we spend in March" is the question
  // the cash flow will ask.
  const foot = useMemo(() => {
    const periods = Array(12).fill(0);
    let opening = 0;
    const years = { 2: 0, 3: 0, 4: 0, 5: 0 };
    rows.forEach((r) => {
      opening += Number(r.opening_balance) || 0;
      r.periods.forEach((v, i) => { periods[i] += Number(v) || 0; });
      OUT_YEARS.forEach((y) => { years[y] += Number(r.years[y]) || 0; });
    });
    return { opening, periods, years };
  }, [rows]);

  const gridRows = rows.length + 1; // + foot

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const isFoot = row === rows.length;

    if (isFoot) {
      if (id === 'description') return gcell.text('TOTAL PROGRAMME', { readonly: true, theme: T_FOOT });
      if (id === 'opening_balance') return gcell.number(foot.opening, { display: fmtR(foot.opening), readonly: true, theme: T_FOOT });
      if (/^P\d+$/.test(id)) {
        const v = foot.periods[Number(id.slice(1)) - 1];
        return gcell.number(v, { display: fmtR(v), readonly: true, theme: T_FOOT });
      }
      if (id === 'year1') {
        const v = foot.periods.reduce((a, b) => a + b, 0);
        return gcell.number(v, { display: fmtR(v), readonly: true, theme: T_FOOT });
      }
      if (/^Y\d$/.test(id)) {
        const v = foot.years[Number(id.slice(1))];
        return gcell.number(v, { display: fmtR(v), readonly: true, theme: T_FOOT });
      }
      if (id === 'total') {
        const v = foot.opening + foot.periods.reduce((a, b) => a + b, 0)
          + OUT_YEARS.reduce((a, y) => a + foot.years[y], 0);
        return gcell.number(v, { display: fmtR(v), readonly: true, theme: T_FOOT });
      }
      return gcell.text('', { readonly: true, theme: T_FOOT });
    }

    const r = rows[row];
    if (!r) return gcell.text('', { readonly: true });
    const base = r._new ? T_NEW : undefined;

    switch (id) {
      case 'capex_ref':
        return gcell.text(r.capex_ref || '', { readonly: false, theme: base });
      case 'description':
        return gcell.text(r.description || '', { readonly: false, theme: base });
      case 'site_code':
        // REQUIRED, not optional. The asset this creates has to live somewhere
        // and the depreciation charge has to land on a cost centre, so the
        // empty state reads as a prompt rather than as "all sites".
        return gcell.text(r.site_code || 'Required', { readonly: true, theme: r.site_code ? base : (base || T_MUTED) });
      case 'asset_class_code':
        return gcell.text(r.asset_class_code || 'Not set', { readonly: true, theme: r.asset_class_code ? base : (base || T_MUTED) });
      case 'project_status':
        return gcell.text(STATUS_LABEL[r.project_status] || r.project_status || 'Proposed', { readonly: true, theme: base });
      case 'opening_balance':
        return gcell.number(r.opening_balance, { display: fmtR(r.opening_balance), readonly: false, theme: base || T_OPENING });
      case 'year1': {
        const v = year1Of(r);
        return gcell.number(v, { display: fmtR(v), readonly: true, theme: base || T_TOTAL });
      }
      case 'total': {
        const v = programmeOf(r);
        return gcell.number(v, { display: fmtR(v), readonly: true, theme: base || T_TOTAL });
      }
      case 'cap':
        // Blank is legitimate on a multi-year project that switches on after
        // the budget year — it reads "Later", not "Not set", because there is
        // nothing to fix.
        return gcell.text(r.capitalisation_period ? `P${r.capitalisation_period}` : 'Later',
          { readonly: true, theme: r.capitalisation_period ? base : (base || T_MUTED) });
      case 'items': {
        const n = (r.items || []).length;
        if (!n) return gcell.text('Not split', { readonly: true, theme: base || T_MUTED });
        const alloc = r.items.reduce((a, i) => a + (Number(i.amount) || 0), 0);
        const cost = (Number(r.opening_balance) || 0) + year1Of(r);
        // "3 · part" reads at a glance: how many, and whether anything is still
        // depreciating on the project default rather than its own class.
        const full = Math.abs(cost - alloc) < 0.005;
        return gcell.text(`${n} item${n === 1 ? '' : 's'}${full ? '' : ' · part'}`,
          { readonly: true, theme: base });
      }
      case 'del':
        return gcell.text('✕', { readonly: true, theme: base });
      default:
        if (/^P\d+$/.test(id)) {
          const v = r.periods[Number(id.slice(1)) - 1] || 0;
          return gcell.number(v, { display: fmtR(v), readonly: false, theme: base });
        }
        if (/^Y\d$/.test(id)) {
          const v = r.years[Number(id.slice(1))] || 0;
          return gcell.number(v, { display: fmtR(v), readonly: false, theme: base || T_OUTYEAR });
        }
        return gcell.text('', { readonly: true, theme: base });
    }
    // programmeOf / year1Of are pure helpers over the row passed in, declared
    // at module scope in spirit — they close over nothing that changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colIds, rows, foot]);

  const rowsRef = useRef([]);
  rowsRef.current = rows;

  const mark = (key) => setDirty((prev) => new Set(prev).add(key));

  const onCellEdited = useCallback(([col, row], v) => {
    if (row >= rowsRef.current.length) return;
    const id = colIds[col];
    const r = rowsRef.current[row];
    const raw = v?.data;

    setRows((prev) => prev.map((x) => {
      if (x._key !== r._key) return x;
      if (id === 'capex_ref') return { ...x, capex_ref: String(raw || '').toUpperCase() };
      if (id === 'description') return { ...x, description: String(raw ?? '') };
      if (id === 'opening_balance') return { ...x, opening_balance: Number(raw) || 0 };
      if (/^P\d+$/.test(id)) {
        const periods = [...x.periods];
        periods[Number(id.slice(1)) - 1] = Number(raw) || 0;
        return { ...x, periods };
      }
      if (/^Y\d$/.test(id)) {
        return { ...x, years: { ...x.years, [Number(id.slice(1))]: Number(raw) || 0 } };
      }
      return x;
    }));
    mark(r._key);
  }, [colIds]);

  const onCellClicked = useCallback(([col, row], event) => {
    if (row >= rowsRef.current.length) return;
    const id = colIds[col];
    const r = rowsRef.current[row];
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };

    if (id === 'items') { setItemsFor(r); return; }

    if (id === 'del') {
      setRows((prev) => prev.filter((x) => x._key !== r._key));
      if (!r._new) {
        capexService.remove(r.capex_ref)
          .then((res) => setNotice(res.message || 'Project removed.'))
          .catch((e) => setError(e.response?.data?.message || 'Could not remove the project.'));
      }
      return;
    }

    const pickable = {
      site_code: (picks.sites || []).map((s) => ({ code: s.site_code, description: s.site_name })),
      asset_class_code: (picks.assetClasses || []).map((c) => ({
        code: c.asset_class_code,
        // The class carries the depreciation policy, so showing the life here
        // is what stops somebody picking a class whose policy they did not
        // expect and finding out via the depreciation schedule.
        description: `${c.description}${c.default_useful_life_months ? ` · ${c.default_useful_life_months} mths` : ''}`,
      })),
      project_status: (data.statuses || []).map((s) => ({ code: s, description: STATUS_LABEL[s] || s })),
      cap: PERIODS.map((p) => ({ code: String(p), description: periodLabel(p, fyStartMonth) })),
    };
    if (pickable[id]) {
      setPicker({ rowKey: r._key, field: id, options: pickable[id], ...pos });
    }
  }, [colIds, picks, data.statuses, fyStartMonth]);

  const applyPick = (value) => {
    setRows((prev) => prev.map((r) => {
      if (r._key !== picker.rowKey) return r;
      if (picker.field === 'cap') return { ...r, capitalisation_period: value ? Number(value) : null };
      return { ...r, [picker.field]: value };
    }));
    mark(picker.rowKey);
    setPicker(null);
  };

  // Changing the account at the top is a change to every project that does not
  // override it — so they all become unsaved, and Save writes them.
  const applyWip = (code) => {
    setWipAccount(code || '');
    setRows((prev) => prev.map((r) => ({ ...r, gl_wip_account: code || null })));
    setDirty(new Set(rows.map((r) => r._key)));
  };

  const save = async () => {
    const toSave = rows.filter((r) => dirty.has(r._key));
    if (!toSave.length) { setNotice('Nothing has changed.'); return; }

    const bad = toSave.find((r) => !r.capex_ref || !r.description || !r.site_code);
    if (bad) {
      setError(`${bad.capex_ref || 'A new project'} is missing a reference, a description or a site — all three are required.`);
      return;
    }

    setSaving(true);
    setError('');
    setNotice('');
    try {
      for (const r of toSave) {
        // eslint-disable-next-line no-await-in-loop
        await capexService.save({
          capex_ref: r.capex_ref,
          description: r.description,
          site_code: r.site_code || null,
          asset_class_code: r.asset_class_code || null,
          cost_center_code: r.cost_center_code || null,
          project_status: r.project_status || 'PROPOSED',
          capitalisation_period: r.capitalisation_period || null,
          opening_balance: Number(r.opening_balance) || 0,
          is_committed: !!r.is_committed,
          gl_asset_cost_account: r.gl_asset_cost_account || null,
          // Every project budgets against the account chosen at the top unless
          // it already carries its own.
          gl_wip_account: r.gl_wip_account || wipAccount || null,
          notes: r.notes || null,
          items: r.items || [],
          periods: r.periods.map((amount, i) => ({ financial_period: i + 1, amount })),
          years: OUT_YEARS.map((y) => ({ year_offset: y, amount: Number(r.years[y]) || 0 })),
        });
      }
      setNotice(`${toSave.length} project${toSave.length === 1 ? '' : 's'} saved. Spend accumulates in ${wipAccount || 'WIP'} and clears to the asset account when each project capitalises.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => ({
    opening: foot.opening,
    year1: foot.periods.reduce((a, b) => a + b, 0),
    out: OUT_YEARS.reduce((a, y) => a + foot.years[y], 0),
  }), [foot]);

  // Only a problem when the project FINISHES spending in year 1 and still has
  // no capitalisation month. A multi-year project with out-year spend is
  // supposed to have none — it switches on after this budget, and warning about
  // it would train people to ignore the banner.
  const noCapPeriod = rows.filter((r) =>
    year1Of(r) > 0 && !r.capitalisation_period && outYearsOf(r) === 0).length;

  const noSite = rows.filter((r) => !r.site_code).length;

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to plan capital.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate('/capital')}>
              <ArrowLeft className="w-3.5 h-3.5" /> Capital
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              Capital Programme
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
            {dirty.size > 0 && (
              <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                {dirty.size} unsaved
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* WHAT YOU ARE BUDGETING AGAINST. Spend accumulates here while the
                project is built and clears to the asset account the month it is
                switched on — so a project mid-build shows on the balance sheet
                as work in progress instead of vanishing between the bank and
                the fixed asset register. */}
            <div className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
              <span className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 dark:bg-slate-900">
                WIP account
              </span>
              <select
                className="px-2 py-1.5 text-xs bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border-0 focus:ring-0"
                value={wipAccount}
                onChange={(e) => applyWip(e.target.value)}
                title="Where capital spend accumulates until the project capitalises. Applies to every project in this programme."
              >
                <option value="">Not tracked</option>
                {(picks.wipAccounts || []).map((a) => (
                  <option key={a.account_code} value={a.account_code}>
                    {a.account_code} · {a.account_description}
                  </option>
                ))}
              </select>
            </div>
            <Figure label="Opening" value={fmtR(totals.opening)} />
            <Figure label={fy ? `FY${fy}` : 'Year 1'} value={fmtR(totals.year1)} tone="key" />
            <Figure label="Years 2–5" value={fmtR(totals.out)} />
            <button className={btn} onClick={() => { const r = blankRow(); setRows((p) => [...p, r]); mark(r._key); }}>
              <Plus className="w-3.5 h-3.5" /> Add project
            </button>
            <button className={btnPrimary} onClick={save} disabled={saving || loading || dirty.size === 0}>
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button className={btn} onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {error && (
          <div className="rounded-lg border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
        {notice && (
          <div className="rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-emerald-700 dark:text-emerald-300">{notice}</p>
          </div>
        )}
        {noSite > 0 && (
          <div className="rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
              <TriangleAlert className="w-3.5 h-3.5" />
              {noSite} project{noSite === 1 ? '' : 's'} without a site
            </p>
            <p className="m-0 mt-0.5 text-[11px] text-amber-700/80 dark:text-amber-300/80">
              A site is required &mdash; the asset this creates has to live somewhere, and the depreciation charge has
              to land on a cost centre.
            </p>
          </div>
        )}
        {noCapPeriod > 0 && (
          <div className="rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
              <TriangleAlert className="w-3.5 h-3.5" />
              {noCapPeriod} project{noCapPeriod === 1 ? ' finishes' : 's finish'} this year with no capitalisation month
            </p>
            <p className="m-0 mt-0.5 text-[11px] text-amber-700/80 dark:text-amber-300/80">
              The cash still leaves, but nothing starts depreciating &mdash; so the cash flow moves and the P&amp;L
              does not. (A project with spend in years 2&ndash;5 is not flagged: it is supposed to switch on later.)
            </p>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <MantleDataGrid
              gridId="capital-programme"
              fitHeight
              fitHeightPadding={6}
              columns={columns}
              rowCount={gridRows}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              freezeColumns={2}
              hint="Type into Opening, the twelve months and Years 2–5. Click Site, Class, Status or Capitalise to pick. Click ITEMS to break a project into the things it creates — each depreciates on its own class, which matters when a project buys a 15-year line and a 5-year vehicle. Year 1 and Programme are computed. ✕ removes a project."
              toolbar={
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  {rows.length} project{rows.length === 1 ? '' : 's'} · years 2–5 are planning only and reach no statement
                </span>
              }
            />
          )}
        </div>

        <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
          <strong>Only year 1 reaches the statements.</strong> The twelve months feed the cash flow at the month the
          money leaves, and depreciation starts in the capitalisation month. Years 2&ndash;5 exist so the programme
          can be seen whole &mdash; nothing reads them but this page.
        </p>
        <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
          <strong>Spend sits in work in progress until the project capitalises.</strong> Each month&rsquo;s spend
          debits the WIP account above; in the capitalisation month the whole project &mdash; prior-year opening
          balance included &mdash; clears out of WIP and into the asset account, and depreciation starts. A project
          with no capitalisation month never leaves WIP, which is correct: it is not finished. Set the account to
          &ldquo;Not tracked&rdquo; and spend appears in PPE only at capitalisation, which is how it behaved before.
        </p>
        <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
          <strong>Opening balance is cost but not cash.</strong> Spend already incurred in prior years is part of what
          the asset cost, so it depreciates from the capitalisation month &mdash; but it left the bank last year, so it
          is not in this year&rsquo;s cash flow. That is why the depreciable cost of a project can exceed what it
          spends this year.
        </p>
      </div>

      {itemsFor && (
        <CapexItemsModal
          project={itemsFor}
          assetClasses={picks.assetClasses || []}
          onClose={() => setItemsFor(null)}
          onSave={(items) => {
            setRows((prev) => prev.map((r) => (r._key === itemsFor._key ? { ...r, items } : r)));
            mark(itemsFor._key);
            setItemsFor(null);
            setNotice('Items updated — press Save to write them.');
          }}
        />
      )}

      {picker && (
        <PickerPopover
          anchor={{ x: picker.x, y: picker.y }}
          options={picker.options}
          placeholder="Search…"
          emptyText="Nothing matches."
          onPick={(o) => applyPick(o.code)}
          onClear={() => applyPick(null)}
          clearLabel="Clear"
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

const Figure = ({ label, value, tone }) => (
  <div className={`inline-flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border ${
    tone === 'key'
      ? 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300'
      : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200'}`}>
    <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</span>
    <span className="text-sm font-bold tabular-nums">{value}</span>
  </div>
);
